import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, CheckCircle, FileText, Camera, Landmark, Scan, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const docTypes = [
  { type: "passport", label: "Passport Copy", icon: FileText, desc: "Clear scan of passport bio page", scannable: true },
  { type: "photo", label: "Passport Photo", icon: Camera, desc: "Recent passport-sized photo", scannable: false },
  { type: "bank_statement", label: "Bank Statement", icon: Landmark, desc: "Last 3 months bank statements", scannable: false },
];

export default function DocumentUploadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState("");
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => { fetchDocs(); }, [id]);

  const fetchDocs = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/documents/${id}`,  { headers: { Authorization: `Bearer ${token}` } });
      setDocs(data);
    } catch {}
  };

  const handleUpload = async (docType, file) => {
    setUploading(docType);
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("application_id", id);
      form.append("document_type", docType);
      form.append("file", file);
      await axios.post(`${API}/documents/upload`, form, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      fetchDocs();

      // Auto OCR for passport
      if (docType === "passport" && file.type.startsWith("image/")) {
        setOcrLoading(true);
        try {
          const ocrForm = new FormData();
          ocrForm.append("file", file);
          const { data: ocr } = await axios.post(`${API}/documents/ocr`, ocrForm, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
          setOcrResult(ocr);
        } catch {} finally { setOcrLoading(false); }
      }
    } catch {} finally { setUploading(""); }
  };

  const uploaded = docs.map(d => d.document_type);

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="text-center mb-10">
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Upload Documents</h1>
          <p className="text-sm text-slate-500">Upload required documents for your application</p>
        </div>

        <div className="space-y-4 mb-8">
          {docTypes.map(dt => {
            const isUploaded = uploaded.includes(dt.type);
            const isUploading = uploading === dt.type;
            const Icon = dt.icon;
            return (
              <motion.div key={dt.type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`card-elevated p-5 ${isUploaded ? "border-emerald-200" : ""}`} data-testid={`doc-${dt.type}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isUploaded ? "bg-emerald-50" : "bg-slate-100"}`}>
                      {isUploaded ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <Icon className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{dt.label}</p>
                      <p className="text-xs text-slate-400">{dt.desc}</p>
                    </div>
                  </div>
                  {!isUploaded && (
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => e.target.files[0] && handleUpload(dt.type, e.target.files[0])} />
                      <span className="text-xs font-semibold text-blue-600 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-50 inline-flex items-center gap-1">
                        {isUploading ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3" /> Upload</>}
                      </span>
                    </label>
                  )}
                  {isUploaded && dt.scannable && !ocrResult && !ocrLoading && (
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Uploaded</span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* OCR Loading */}
        {ocrLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-elevated p-6 mb-8 text-center">
            <Scan className="w-8 h-8 text-blue-600 mx-auto mb-3 animate-pulse" />
            <p className="text-sm font-medium text-slate-800">Scanning passport with AI...</p>
            <p className="text-xs text-slate-400 mt-1">Extracting name, passport number, expiry date</p>
          </motion.div>
        )}

        {/* OCR Result */}
        {ocrResult && ocrResult.success && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="card-elevated p-6 mb-8 border-blue-200" data-testid="ocr-result">
            <div className="flex items-center gap-2 mb-4">
              <Scan className="w-5 h-5 text-blue-600" />
              <h3 className="text-sm font-semibold text-slate-900">AI Passport Verification</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ocrResult.data.verification_status === "valid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                {ocrResult.data.verification_status === "valid" ? "VERIFIED" : "NEEDS ATTENTION"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[["Full Name", ocrResult.data.full_name], ["Passport #", ocrResult.data.passport_number],
                ["Nationality", ocrResult.data.nationality], ["Date of Birth", ocrResult.data.date_of_birth],
                ["Expiry Date", ocrResult.data.expiry_date], ["Gender", ocrResult.data.gender]
              ].map(([l, v]) => v && (
                <div key={l} className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-[10px] text-slate-400">{l}</p>
                  <p className="text-sm text-slate-800 font-medium">{v}</p>
                </div>
              ))}
            </div>
            {ocrResult.data.issues && ocrResult.data.issues.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /><span className="text-xs font-semibold text-amber-700">Issues Found</span></div>
                <ul className="space-y-1">
                  {ocrResult.data.issues.map((issue, i) => <li key={i} className="text-xs text-amber-600 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-amber-500" />{issue}</li>)}
                </ul>
              </div>
            )}
          </motion.div>
        )}

        <button onClick={() => navigate(`/apply/${id}/payment`)} data-testid="proceed-payment"
          className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all">
          Proceed to Payment
        </button>
      </div>
    </div>
  );
}
